(ns app.front.view.pdf-markup.drawing
  "Shared drawing controls for tool defaults and selection styling."
  (:require [app.front.ui :as ui]
            [app.front.ui.dropdown :as ui-dd]
            [app.front.util :as futil]
            [app.front.view.pdf-markup.embedpdf :as epdf]
            [core.front.carbon :as cc]
            [potpuri.core :as p]
            [reagent.core :as r]))

(def tool-groups
  [{:id           :freehand
    :label-tr-key :pdf-drawing/freehand
    :dropdown?    false
    :tools        [["ink" :attachment/tool-draw] ["ink-highlight" :pdf-drawing/highlighter]]}
   {:id           :line-arrow
    :label-tr-key :pdf-drawing/line-arrow
    :dropdown?    true
    :tools        [["arrow" :pdf-drawing/arrow] ["line" :attachment/tool-line]]}
   {:id               :shapes
    :label-tr-key     :pdf-drawing/shapes
    :dropdown?        true
    :tools            [["square" :attachment/tool-square] ["circle" :attachment/tool-circle]
                       ["polygon" :pdf-drawing/polygon] ["polyline" :pdf-drawing/polyline]]
    :standalone-tools [["cloud" :pdf-drawing/cloud]]}])

(def arrow-preset
  {:id "arrow" :extends "line" :defaults {:lineEndings {:start "none" :end "open-arrow"}}})

(def filled-shape-defaults
  {:interiorColor "#ff6666" :opacity 0.6})

(def filled-shape-presets
  (mapv #(hash-map :id % :defaults filled-shape-defaults) ["square" "circle" "polygon"]))

(def cloud-preset
  {:id          "cloud"
   :extends     "square"
   :clickCreate false
   :defaults    (assoc filled-shape-defaults :borderStyle "solid" :cloudyIntensity 1)})

(def highlighter-preset
  ;; Merge into the native tool once at initialization, not on each activation.
  {:id "ink-highlight" :defaults {:opacity 0.6}})

(def tool-paths
  {"ink"           "M3 17C7 5 9 5 8 13S13 20 16 10C17 6 20 7 21 9"
   "ink-highlight" "M4 15L13 6L18 11L9 20H4ZM13 6L15 4L20 9L18 11M3 22H13"
   "line"          "M4 20L20 4"
   "arrow"         "M4 20L20 4M9 4H20V15"
   "square"        "M4 4H20V20H4Z"
   "cloud"         (str "M5 4Q3 1 2 5Q0 7 3 9Q0 12 3 14Q0 17 3 19Q3 23 7 21Q10 24 12 21"
                        "Q15 24 17 21Q21 23 21 19Q24 17 21 14Q24 11 21 9Q24 6 21 5"
                        "Q21 1 17 3Q14 0 12 3Q8 0 7 3Q6 2 5 4Z")
   "circle"        "M12 3A9 9 0 1 0 12 21A9 9 0 1 0 12 3"
   "polygon"       "M3 9L11 3L21 8L17 21L5 18Z"
   "polyline"      "M3 18L8 6L15 17L21 4"})

(def editable-keys #{:color :strokeWidth :interiorColor :opacity :borderStyle :lineEndings})

(def ^:private property-label-tr-keys
  {:color         :pdf-drawing/color
   :interiorColor :pdf-drawing/interior-color
   :strokeWidth   :pdf-drawing/stroke-width
   :opacity       :pdf-drawing/opacity
   :borderStyle   :pdf-drawing/border})

(def ^:private option-label-tr-keys
  {"solid"          :pdf-drawing/solid
   "dashed"         :pdf-drawing/dashed
   "dotted"         :pdf-drawing/dotted
   "cloudy"         :pdf-drawing/cloudy
   "none"           :pdf-drawing/none
   "square"         :pdf-drawing/square
   "circle"         :pdf-drawing/circle
   "diamond"        :pdf-drawing/diamond
   "open-arrow"     :pdf-drawing/open-arrow
   "closed-arrow"   :pdf-drawing/closed-arrow
   "butt"           :pdf-drawing/butt
   "r-open-arrow"   :pdf-drawing/r-open-arrow
   "r-closed-arrow" :pdf-drawing/r-closed-arrow
   "slash"          :pdf-drawing/slash
   "mixed"          :pdf-drawing/mixed})

(defn property-data [data]
  (let [{:keys [properties values mixed]} (js->clj data :keywordize-keys true)]
    {:specs  (mapv #(update % :key keyword) properties)
     :values values
     :mixed  (mapv keyword mixed)}))

(def endings
  ["none" "square" "circle" "diamond" "open-arrow" "closed-arrow"
   "butt" "r-open-arrow" "r-closed-arrow" "slash"])

(defn numeric-value [{:keys [min max]} text]
  (when-let [value (futil/parse-float text)]
    (when (<= min value max) value)))

(def border-choice->embed-pdf-value
  {"solid"  {:borderStyle "solid" :dashArray nil :cloudyIntensity nil}
   "dashed" {:borderStyle "dashed" :dashArray [6 3] :cloudyIntensity nil}
   "dotted" {:borderStyle "dashed" :dashArray [1 3] :cloudyIntensity nil}
   "cloudy" {:borderStyle "solid" :dashArray nil :cloudyIntensity 1}})

(defn embed-pdf-value->border-choice [{:keys [borderStyle dashArray cloudyIntensity]}]
  (cond
    (and cloudyIntensity (pos? cloudyIntensity)) "cloudy"
    (and (= borderStyle "dashed") (= dashArray [1 3])) "dotted"
    :else borderStyle))

(defn merge-property-patch [values patch]
  ;; EmbedPDF replaces lineEndings as a whole, while each control edits just one end.
  (if (:lineEndings patch)
    (update patch :lineEndings #(merge (:lineEndings values) %))
    patch))

(defn selection-property-patch [patch]
  (if (:lineEndings patch)
    (fn [^js annotation]
      (clj->js (merge-property-patch
                 {:lineEndings (js->clj (.-lineEndings annotation) :keywordize-keys true)}
                 patch)))
    (clj->js patch)))

(defn primary-tool [tools active-id]
  (or (p/find-first tools #(= active-id (first %))) (first tools)))

(defn- tool-icon [id]
  [:svg {:width           20
         :height          20
         :viewBox         "0 0 24 24"
         :fill            "none"
         :stroke          "currentColor"
         :stroke-width    1.75
         :stroke-linecap  "round"
         :stroke-linejoin "round"
         :aria-hidden     true
         :focusable       false}
   [:path {:d              (get tool-paths id)
           :data-icon-path true}]])

(def ^:private tool-icon-components
  (into {} (map (fn [id]
                  [id (fn [^js props]
                        (r/as-element
                          (update (tool-icon id) 1 assoc
                                  :class (.-className props)
                                  :style {:width 20 :height 20 :fill "none"})))])
                (keys tool-paths))))

(def selected-tool-class
  "[&:not(:hover):not(:focus):not(:active):not(:disabled)]:!bg-gray-200")

(defn- drawing-button [{:keys [tr active-id on-tool disabled?]} [id label]]
  (cc/icon-button
    {:kind             :ghost
     :size             :small
     :icon-description (tr [label])
     :is-selected      (= active-id id)
     :disabled         (boolean disabled?)
     :render-icon      (get tool-icon-components id)
     :class-name       (str "!px-4 [&_svg_path]:!fill-none focus:!border-ui-violet-20 "
                            (when (= active-id id)
                              selected-tool-class))
     :on-click         #(on-tool id)}))

(defn- grouped-tools [{:keys [tr active-id on-tool disabled?] :as props} label-tr-key tools]
  (let [label (tr [label-tr-key])]
    [:div.flex.items-center
     [drawing-button props (primary-tool tools active-id)]
     [ui-dd/dropdown {:align                  "left"
                      :open-icon-description  label
                      :close-icon-description label
                      :toggle-props           {:class    (str "bx--btn bx--btn--sm bx--btn--ghost px-2"
                                                              (when disabled? " bx--btn--disabled"))
                                               :disabled (boolean disabled?)
                                               :title    label}
                      :options-props          {:class "bg-white border border-gray-300 shadow-lg p-1"}}
      nil
      (futil/for-frag [[id label-key] tools]
        [ui/button {:kind       :tertiary
                    :size       :small
                    :disabled   (boolean disabled?)
                    :class-name "whitespace-nowrap gap-2"
                    :on-click   #(on-tool id)}
         [tool-icon id]
         (tr [label-key])])]]))

(defn toolbar [{:keys [tr] :as props}]
  [:div.flex.flex-wrap.gap-2
   (futil/for-frag [{:keys [label-tr-key tools dropdown? standalone-tools]} tool-groups]
     [:div.flex.items-center.border-l.border-gray-300.px-2
      {:role "group" :aria-label (tr [label-tr-key])}
      (if dropdown?
        [grouped-tools props label-tr-key tools]
        (futil/for-frag [tool tools] [drawing-button props tool]))
      (futil/for-frag [tool standalone-tools]
        [drawing-button props tool])])])

(defn border-options [cloudy]
  (cond-> ["solid" "dashed" "dotted"] cloudy (conj "cloudy")))

(defn opacity-value [text]
  (some-> (numeric-value {:min 0 :max 100} text) js/Math.round (/ 100)))

(defn- toolbar-number-input
  "Preserves draft text while editing so asynchronous engine updates do not overwrite keystrokes."
  [props]
  (r/with-let [*draft (r/atom nil)]
    [ui/number-input (assoc props
                            :value (or @*draft (:value props))
                            :on-blur #(reset! *draft nil)
                            :on-key-up #(when (= "Enter" (.-key %)) (.blur (.-target %)))
                            :on-change (fn [event]
                                         (reset! *draft (futil/tval event))
                                         ((:on-change props) event)))]))

(defn- option-preview [preview-kind value]
  [:svg {:width        80
         :height       28
         :viewBox      "0 0 80 28"
         :fill         "none"
         :stroke       "currentColor"
         :stroke-width 1.5
         :aria-hidden  true
         :focusable    false}
   (if (= preview-kind :border)
     (if (= value "cloudy")
       [:path {:d "M8 16Q8 7 16 12Q20 4 26 12Q32 4 38 12Q44 4 50 12Q56 4 62 12Q70 7 72 16"}]
       [:path {:d                "M8 14H72"
               :stroke-dasharray (case value "dashed" "6 3" "dotted" "1 3" nil)}])
     ;; Schematic line-ending previews; EmbedPDF renders the actual annotations.
     [:g {:transform (when (= preview-kind :start-ending) "translate(80 28) rotate(180)")}
      [:path {:d "M10 14H56"}]
      [:g {:transform "translate(56 14)"
           :fill      "white"}
       (case value
         "square" [:rect {:x      -4.5
                          :y      -4.5
                          :width  9
                          :height 9}]
         "circle" [:circle {:r 3.75}]
         "diamond" [:path {:d "M4.5 0L0 4.5L-4.5 0L0 -4.5Z"}]
         "butt" [:path {:d "M0 -4.5V4.5"}]
         "slash" [:path {:d "M6.75 -11.69L-6.75 11.69"}]
         ("open-arrow" "closed-arrow" "r-open-arrow" "r-closed-arrow")
         [:path {:transform (when (#{"r-open-arrow" "r-closed-arrow"} value) "rotate(180)")
                 :fill      "none"
                 :d         (str "M-11.69 6.75L0 0L-11.69 -6.75"
                                 (when (#{"closed-arrow" "r-closed-arrow"} value) "Z"))}]
         nil)]])])

(defn- property-dropdown [{:keys [tr id label-tr-key preview-kind value options on-change mixed?]}]
  (let [title (tr [label-tr-key])
        text  #(tr [(option-label-tr-keys %)])]
    [:div
     [:div.text-sm.mb-2 {:id (str id "-label")} title]
     [ui-dd/dropdown
      {:align                  "left"
       :open-icon-description  title
       :close-icon-description title
       :toggle-props           {:class      "bg-white border-b border-gray-500 px-3 h-10"
                                :aria-label (str title ": " (text (if mixed? "mixed" value)))}
       :options-props          {:class "bg-white border border-gray-300 shadow-lg max-h-64 p-1"}}
      (if mixed? [:span.px-2 (text "mixed")] [option-preview preview-kind value])
      (futil/for-frag [option options]
        [ui/button {:kind       :tertiary
                    :class-name "w-full px-3"
                    :aria-label (text option)
                    :title      (text option)
                    :on-click   #(on-change option)}
         [option-preview preview-kind option]])]]))

(defn- color-control
  [{:keys [tr property label value mixed? remembered-fill-color on-remember-fill-color on-change]}]
  (let [fill?    (= property :interiorColor)
        no-fill? (and fill? (not mixed?) (nil? value))
        color    (or value (when fill? remembered-fill-color) "#000000")]
    [:div.flex.items-end.gap-2
     [ui/text-input
      {:type        :color
       :id          (str "pdf-drawing-" (name property))
       :label-text  label
       :light       true
       :input-class "w-16 p-1"
       :value       color
       :on-change   (fn [event]
                      (let [color (futil/tval event)]
                        (when fill? (on-remember-fill-color color))
                        (when-not no-fill? (on-change {property color}))))}]
     (when fill?
       (cc/checkbox {:id         "pdf-drawing-no-fill"
                     :label-text (tr [:pdf-drawing/no-fill])
                     :checked    no-fill?
                     :on-change  (fn [checked?]
                                   (on-remember-fill-color color)
                                   (on-change {:interiorColor (when-not checked? color)}))}))]))

(defn- opacity-control [_]
  (let [*interaction (r/atom nil)]
    (r/create-class
      {:component-did-update
       (fn [this]
         (let [{:keys [mixed? acknowledged-opacity]} (r/props this)]
           ;; Keep the requested value until every selected annotation has acknowledged it.
           (when (and @*interaction (not (:active? @*interaction)) (not mixed?)
                      (number? acknowledged-opacity)
                      (= (js/Math.round (* 100 acknowledged-opacity)) (:percent @*interaction)))
             (reset! *interaction nil))))
       :reagent-render
       (fn [{:keys [tr value mixed? on-change]}]
         (let [label          (tr [:pdf-drawing/opacity])
               display-value  (or (:percent @*interaction)
                                  (if mixed? 0 (js/Math.round (* 100 (or value 0)))))
               display-mixed? (and mixed? (nil? @*interaction))
               publish!       (fn [percent]
                                (swap! *interaction assoc :percent percent :changed? true)
                                (on-change {:opacity (/ percent 100)}))]
           [:div.flex.items-end.gap-2
            [:div.flex.flex-col
             [:label.bx--label {:for "pdf-drawing-opacity"} label]
             [:div.flex.items-center.gap-2.h-10
              [:span.text-sm "0%"]
              [:input.w-48.accent-ui-violet-100
               {:id                "pdf-drawing-opacity"
                :type              "range"
                :min               0
                :max               100
                :step              1
                :value             display-value
                :aria-valuetext    (if display-mixed? (tr [:pdf-drawing/mixed]) (str display-value "%"))
                :on-change         #(publish! (js/Number (futil/tval %)))
                :on-pointer-down   (fn [event]
                                     (when (zero? (.-button event))
                                       (reset! *interaction {:percent display-value  :active? true
                                                             :mixed?  display-mixed?})))
                :on-pointer-up     (fn [_]
                                     (when (:active? @*interaction)
                                       ;; A click at the Mixed placeholder's minimum may emit no input event.
                                       (when (and (:mixed? @*interaction) (not (:changed? @*interaction)))
                                         (publish! (:percent @*interaction)))
                                       (swap! *interaction assoc :active? false)))
                :on-pointer-cancel #(when @*interaction (swap! *interaction assoc :active? false))}]
              [:span.text-sm "100%"]]]
            [:div.w-24
             [toolbar-number-input {:id          "pdf-drawing-opacity-number"
                                    :label-text  label
                                    :hide-label  true
                                    :light       true
                                    :unit-label  "%"
                                    :min         0
                                    :max         100
                                    :step        1
                                    :value       (if display-mixed? "" display-value)
                                    :placeholder (when display-mixed? (tr [:pdf-drawing/mixed]))
                                    :on-change   #(when-let [n (opacity-value (futil/tval %))]
                                                    (reset! *interaction nil)
                                                    (on-change {:opacity n}))}]]]))})))

(defn properties
  "Controls for the native specs shared by the selection.
   A patch changes one property, leaving unrelated mixed values untouched.
   editing-target scopes remembered fill colours and optimistic opacity state to a tool or annotation selection.
   acknowledged-opacity distinguishes engine confirmations from optimistic selection values."
  [{:keys [tr specs values mixed on-change cloud-tool? editing-target] :as props}]
  (r/with-let [*fill-colors (r/atom {})]
    (let [specs (filter #(editable-keys (:key %)) specs)
          mixed (set mixed)]
      (when (seq specs)
        (into [:div.flex.flex-wrap.items-end.gap-4.px-4.py-3.bg-gray-100.border-b]
              (map
                (fn [{:keys [key min max cloudy] :as spec}]
                  (let [value  (get values key)
                        mixed? (contains? mixed key)
                        label  (some-> (property-label-tr-keys key) vector tr)]
                    (case key
                      (:color :interiorColor)
                      [color-control {:tr                     tr
                                      :property               key
                                      :label                  label
                                      :value                  value
                                      :mixed?                 mixed?
                                      :remembered-fill-color  (get @*fill-colors editing-target)
                                      :on-remember-fill-color #(swap! *fill-colors assoc editing-target %)
                                      :on-change              on-change}]

                      :opacity
                      ^{:key editing-target}
                      [opacity-control {:tr                   tr
                                        :value                value
                                        :mixed?               mixed?
                                        :on-change            on-change
                                        :acknowledged-opacity (get props :acknowledged-opacity value)}]

                      :strokeWidth
                      [:div.w-32
                       {:class "[&_input]:!w-full"}
                       [toolbar-number-input
                        {:id          "pdf-drawing-stroke-width"
                         :label-text  label
                         :light       true
                         :hide-unit?  true
                         :min         min
                         :max         max
                         ;; Allow fractional values regardless of the native minimum.
                         :step        "any"
                         :value       (if mixed? "" (or value ""))
                         :placeholder (when mixed? (tr [:pdf-drawing/mixed]))
                         :on-change   #(when-let [n (numeric-value spec (.. % -target -value))]
                                         (on-change {key n}))}]]

                      :borderStyle
                      (if cloud-tool?
                        [:div
                         [:div.text-sm.mb-2 (tr [:pdf-drawing/border])]
                         [:div.py-3 (tr [:pdf-drawing/cloudy])]]
                        [property-dropdown {:tr           tr
                                            :id           "pdf-drawing-border"
                                            :label-tr-key :pdf-drawing/border
                                            :preview-kind :border
                                            :value        (embed-pdf-value->border-choice values)
                                            :options      (border-options cloudy)
                                            :on-change    #(on-change (border-choice->embed-pdf-value %))
                                            :mixed?       mixed?}])

                      :lineEndings
                      [:<>
                       [property-dropdown {:tr           tr
                                           :id           "pdf-drawing-start-ending"
                                           :label-tr-key :pdf-drawing/start-ending
                                           :preview-kind :start-ending
                                           :value        (:start value)
                                           :options      endings
                                           :on-change    #(on-change {:lineEndings {:start %}})
                                           :mixed?       mixed?}]
                       [property-dropdown {:tr           tr
                                           :id           "pdf-drawing-end-ending"
                                           :label-tr-key :pdf-drawing/end-ending
                                           :preview-kind :end-ending
                                           :value        (:end value)
                                           :options      endings
                                           :on-change    #(on-change {:lineEndings {:end %}})
                                           :mixed?       mixed?}]]))) specs))))))

(defn live-properties [{:keys [tr]}]
  (let [^js interaction-state (epdf/useInteractionState)
        ^js annotation        (epdf/useAnnotation)
        ^js annotation-state  (epdf/useAnnotationState)
        active-id             (.-activeToolId interaction-state)
        selected-annotations  (.-selected annotation-state)
        editing-selection?    (pos? (count selected-annotations))
        selected-opacities    (set (map #(or (.-opacity ^js %) 1) selected-annotations))
        data                  (property-data
                                (epdf/useAnnotationProperties (if editing-selection? js/undefined active-id)))]
    [properties (assoc data :tr tr :cloud-tool? (and (not editing-selection?) (= "cloud" active-id))
                       ;; v3 selection values are optimistic; wait for every selected write to settle.
                       :acknowledged-opacity (if editing-selection?
                                               (when (and (= 1 (count selected-opacities))
                                                          (not-any? #(.isPending annotation (.-ref ^js %))
                                                                    selected-annotations))
                                                 (first selected-opacities))
                                               (get-in data [:values :opacity]))
                       :editing-target (if editing-selection?
                                         (->> selected-annotations
                                              (map #(epdf/refKey (.-ref ^js %)))
                                              sort
                                              vec
                                              str)
                                         active-id)
                       :on-change #(if editing-selection?
                                     (.update (.-selection annotation)
                                              (selection-property-patch %))
                                     (.updateDefaults (.-tools annotation) active-id
                                                      (clj->js (merge-property-patch (:values data) %)))))]))

(defn draft-actions [{:keys [tr can-finish? on-finish on-cancel]}]
  [:div.flex.gap-1.bg-white.p-2.shadow-lg
   [ui/button {:kind :primary :size :small :disabled (not can-finish?) :on-click on-finish}
    (tr [:pdf-drawing/finish])]
   [ui/button {:kind :tertiary :size :small :on-click on-cancel} (tr [:cancel])]])

(defn draft-menu [{:keys [tr]}]
  (let [^js annotation (epdf/useAnnotation)]
    [:> epdf/AnnotationDraftMenu
     (fn [^js anchor]
       ;; EmbedPDF's render callback expects a React element, not Reagent hiccup.
       (r/as-element [draft-actions {:tr          tr
                                     :can-finish? (.-canFinish anchor)
                                     :on-finish   #(.finish (.-draft annotation))
                                     :on-cancel   #(.cancel (.-draft annotation))}]))]))
